import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { Options, V2AdminCreateHelpArticleData } from '@workspace/api-backend'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { useState } from 'react'
import {
  useAdminCreateHelpArticleMutation,
  useAdminHelpCategoriesQuery,
} from '../../../features/admin/hooks/use-admin-api'
import {
  HelpArticleForm,
  type HelpArticleFormData,
} from '../../../features/help-center/components/help-article-form'

type SearchParams = { categoryId?: string }

export const Route = createFileRoute('/admin/help-center/new')({
  component: Page,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    categoryId: (search.categoryId as string) || undefined,
  }),
})

function Page() {
  const { categoryId } = Route.useSearch()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const categoriesQuery = useAdminHelpCategoriesQuery()
  const createMutation = useAdminCreateHelpArticleMutation()

  const categories = (categoriesQuery.data?.data?.items ?? []).map((category) => ({
    id: category.id,
    name: category.name,
  }))

  const handleSubmit = (data: HelpArticleFormData) => {
    createMutation.mutate(
      {
        body: {
          title: data.title,
          content: data.content,
          categoryId: data.categoryId,
          description: data.description || undefined,
          status: data.status,
        },
      } as Options<V2AdminCreateHelpArticleData>,
      {
        onSuccess: (response) => {
          if (response.status === 'ok') {
            navigate({ to: '/admin/help-center/$id', params: { id: response.data.id } })
          }
        },
        onError: () => {
          setErrorMessage('Failed to create article')
          setTimeout(() => setErrorMessage(null), 3000)
        },
      }
    )
  }

  if (!categoriesQuery.isLoading && categories.length === 0) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-4">
        <h1 className="text-2xl font-bold">New Help Article</h1>
        <p className="text-sm text-muted-foreground">
          Create a category first before adding help articles.{' '}
          <Link to="/admin/help-center" className="text-primary hover:underline">
            Go to Help Center
          </Link>
          .
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">New Help Article</h1>
        <CtaButton
          size="sm"
          type="submit"
          form="help-article-new-form"
          loading={createMutation.isPending}
        >
          Save
        </CtaButton>
      </div>

      <HelpArticleForm
        formId="help-article-new-form"
        categories={categories}
        defaultCategoryId={categoryId}
        onSubmit={handleSubmit}
      />

      {errorMessage && (
        <div className="fixed bottom-6 right-6 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg">
          {errorMessage}
        </div>
      )}
    </div>
  )
}
